<template>
  <r-ai-page-container :current-step="4">
    <v-row class="text-start">
      <v-col cols="12">
        <div class="pb-4 text-body-1">
          {{ textIntro }}
        </div>
        <div>
          <r-ai-bubble>
            <div v-if="isEditing" class="text-body-2 text--secondary pt-2 pb-2">
              Click X to remove a skill, or add new skills below.
            </div>
            <v-chip
              v-for="skill in softSkills"
              :key="skill.id"
              class="ma-2 ai-chip"
              color="#dbeafe"
              :close="isEditing"
              @click:close="removeSkill(skill.id)"
            >
              {{ skill.text }}
            </v-chip>
            <div v-if="isEditing" class="pt-2">
              <v-divider></v-divider>
              <div class="pt-4 pb-1 text-body-1">Add New Skill:</div>
              <!-- PR-AGENT-UX: Lightcast-backed soft-skill autocomplete.
                   Removes Michael's "leadership skills input too long and
                   tedious" complaint. -->
              <div class="d-flex">
                <v-combobox
                  v-model="newSkill"
                  :items="softSkillSuggestions"
                  :search-input.sync="softSkillSearch"
                  hide-no-data
                  hide-selected
                  hide-details
                  outlined
                  dense
                  clearable
                  placeholder="Type a skill (e.g. Leadership, Stakeholder Management)"
                  @keyup.enter="addSkill"
                ></v-combobox>
                <v-btn
                  class="ml-2 white-text"
                  color="#00a63e"
                  @click="addSkill"
                >
                  Add
                </v-btn>
              </div>
            </div>
          </r-ai-bubble>
          <div class="pt-2">
            <div v-if="isEditing">
              <v-btn
                class="mt-2 mb-2"
                color="primary"
                outlined
                @click="save()"
                block
              >
                Save and Continue
              </v-btn>
            </div>
            <div v-else>
              <v-btn class="my-4" color="primary" @click="next()" block>
                Looks Good
              </v-btn>
              <v-btn
                class="my-4 ai-outlined-btn"
                outlined
                @click="edit()"
                block
              >
                Edit or Add Info
              </v-btn>
              <v-btn
                class="my-4 ai-outlined-btn"
                outlined
                @click="saveAndExitToDone"
                block
              >
                Save and Exit Agent
              </v-btn>
              <!-- PR-AGENT-ESCAPE: secondary link to the main soft-skills
                   editor. For users who'd rather use the regular UI than
                   continue the guided flow. -->
              <div class="text-center mt-3">
                <a
                  href="#"
                  class="text-caption text--secondary"
                  @click.prevent="$router.push('/softskills')"
                >
                  Switch to manual entry
                </a>
              </div>
            </div>
          </div>
        </div>
      </v-col>
    </v-row>
  </r-ai-page-container>
</template>

<script>
import { mapFields } from "vuex-map-fields";
import { mapActions } from "vuex";
import * as UUID from "../../utility/guid";
import { Get } from "../../api/ApiBase";

export default {
  name: "AILeadershipSkills",
  components: {},
  data: () => ({
    textIntro: "Here's what I found for your leadership skills:",
    isEditing: false,
    newSkill: "",
    // PR-AGENT-UX: soft-skill autocomplete via /api/SkillAutocomplete?type=soft.
    softSkillSearch: "",
    softSkillSuggestions: [],
    _softSkillDebounce: null
  }),
  watch: {
    softSkillSearch(val) {
      if (!val || val.length < 2) {
        this.softSkillSuggestions = [];
        return;
      }
      if (this._softSkillDebounce) clearTimeout(this._softSkillDebounce);
      var self = this;
      this._softSkillDebounce = setTimeout(function() {
        self.fetchSkillSuggestions(val);
      }, 250);
    }
  },
  beforeDestroy() {
    if (this._softSkillDebounce) clearTimeout(this._softSkillDebounce);
  },
  methods: {
    async fetchSkillSuggestions(prefix) {
      try {
        var resp = await Get(
          "/api/SkillAutocomplete?prefix=" +
            encodeURIComponent(prefix) +
            "&type=soft"
        );
        if (
          resp &&
          resp.success &&
          resp.data &&
          Array.isArray(resp.data.results)
        ) {
          this.softSkillSuggestions = resp.data.results
            .map(r => r && r.canonicalName)
            .filter(n => !!n);
        }
      } catch (e) {
        this.softSkillSuggestions = [];
      }
    },
    next() {
      this.$router.push({ path: "/AIEducation" });
    },
    // PR-AGENT-UX: save before exiting so the user's edits aren't lost.
    async saveAndExitToDone() {
      try {
        await this.saveSoftSkills();
      } catch (e) {
        console.warn("saveAndExitToDone failed:", e);
      }
      this.$router.push({ path: "/AIDone" });
    },
    edit() {
      this.isEditing = true;
    },
    save() {
      this.saveSoftSkills();
      this.next();
    },
    ...mapActions("softSkill", ["saveSoftSkills"]),
    addSkill() {
      if (this.newSkill) {
        this.softSkills.push({ id: UUID.gen(), text: this.newSkill, level: 2 });
        this.newSkill = "";
        this.softSkillSearch = "";
        this.softSkillSuggestions = [];
      }
    },
    removeSkill(id) {
      this.softSkills = this.softSkills.filter(skill => skill.id !== id);
    }
  },
  computed: {
    skillsText() {
      return this.softSkillsDisplay ?? "";
    },
    ...mapFields("softSkill", [
      "softSkills",
      "softSkillsDisplay",
      "baseSoftSkills"
    ])
  }
};
</script>

<style scoped>
.edit-bubble {
  background-color: #f9fafb;
  border-radius: 10px;
  padding: 16px;
}

.add-bubble {
  background-color: #eff6ff;
  border-radius: 10px;
  padding: 16px;
}
</style>
